import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../../services/common.service";
import { useCurrencyMaster } from "../../../hooks/useCurrencyMaster";
import { useProductDetailsMaster } from "../../../hooks/useProductDetailsMaster";
import "../../../assets/styles/buyer_rfq.scss";

const DIRECT_PRODUCT_INQUIRY = "direct-product-inquiry";

const isDirectProductInquiry = (rfq) => rfq?.preferences === DIRECT_PRODUCT_INQUIRY;

const parseUploadedFiles = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const parseInquirySpecialRequirements = (text) => {
  if (!text) return { deliveryDate: null, message: null };
  const parts = String(text).split("\n\n");
  let deliveryDate = null;
  const messageParts = [];
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const deliveryMatch = trimmed.match(/^Expected delivery:\s*(.+)$/i);
    if (deliveryMatch) {
      deliveryDate = deliveryMatch[1].trim();
    } else {
      messageParts.push(trimmed);
    }
  }
  return {
    deliveryDate,
    message: messageParts.join("\n\n").trim() || null,
  };
};

const resolveFileUrl = (accessPath) => {
  if (!accessPath) return "";
  if (accessPath.startsWith("http://") || accessPath.startsWith("https://")) {
    return accessPath;
  }
  const baseUrl = window.location.origin;
  let cleanPath = accessPath;
  if (cleanPath.startsWith(":3000")) {
    cleanPath = cleanPath.replace(/^:3000/, "");
  }
  if (!cleanPath.startsWith("/")) {
    cleanPath = `/${cleanPath}`;
  }
  return `${baseUrl}${cleanPath}`;
};

const formatTargetPrice = (rfq, getCurrencyLabel) => {
  const currency = getCurrencyLabel(rfq.target_price_currency);
  const min = rfq.target_price_min;
  const max = rfq.target_price_max;
  if (min == null && max == null) return "—";
  const prefix = currency ? `${currency} ` : "₹";
  if (min != null && max != null && Number(min) === Number(max)) {
    return `${prefix}${min}`;
  }
  if (min != null && max != null) {
    return `${prefix}${min} - ${max}`;
  }
  return `${prefix}${min ?? max}`;
};

function DirectProductInquiryViewModal({ open, rfq, loading, onClose, getUnitLabel, getCurrencyLabel }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const { deliveryDate, message } = parseInquirySpecialRequirements(rfq?.special_requirements);
  const files = parseUploadedFiles(rfq?.uploaded_files);
  const productLink = rfq?.source_product_id
    ? `/marketplace/products/${rfq.source_product_id}`
    : null;

  return (
    <div className="buyer-enquiry-view-modal" role="presentation" onClick={onClose}>
      <div
        className="buyer-enquiry-view-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="buyer-enquiry-view-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="buyer-enquiry-view-modal__header">
          <div>
            <h2 id="buyer-enquiry-view-title" className="buyer-enquiry-view-modal__title">
              Enquiry Details
            </h2>
            <p className="buyer-enquiry-view-modal__subtitle">
              Submitted product enquiry #{rfq?.rfq_id || "—"}
            </p>
          </div>
          <button type="button" className="buyer-enquiry-view-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="buyer-enquiry-view-modal__body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : (
            <div className="buyer-enquiry-view-modal__grid">
              <div className="buyer-enquiry-view-modal__field buyer-enquiry-view-modal__field--full">
                <span className="buyer-enquiry-view-modal__label">Product</span>
                <span className="buyer-enquiry-view-modal__value">
                  {rfq?.product_name || "—"}
                  {productLink ? (
                    <>
                      {" "}
                      <Link to={productLink}>View product</Link>
                    </>
                  ) : null}
                </span>
              </div>

              <div className="buyer-enquiry-view-modal__field">
                <span className="buyer-enquiry-view-modal__label">Quantity</span>
                <span className="buyer-enquiry-view-modal__value">
                  {rfq?.product_quantity ?? "—"} {getUnitLabel(rfq?.quantity_unit)}
                </span>
              </div>

              <div className="buyer-enquiry-view-modal__field">
                <span className="buyer-enquiry-view-modal__label">Target Price</span>
                <span className="buyer-enquiry-view-modal__value">
                  {formatTargetPrice(rfq || {}, getCurrencyLabel)}
                </span>
              </div>

              <div className="buyer-enquiry-view-modal__field">
                <span className="buyer-enquiry-view-modal__label">Expected Delivery</span>
                <span className="buyer-enquiry-view-modal__value">
                  {deliveryDate || (rfq?.lead_time_days ? `${rfq.lead_time_days} day(s)` : "—")}
                </span>
              </div>

              <div className="buyer-enquiry-view-modal__field">
                <span className="buyer-enquiry-view-modal__label">Status</span>
                <span className="buyer-enquiry-view-modal__value">
                  {(rfq?.status || "pending").toUpperCase()}
                </span>
              </div>

              <div className="buyer-enquiry-view-modal__field">
                <span className="buyer-enquiry-view-modal__label">Submitted On</span>
                <span className="buyer-enquiry-view-modal__value">
                  {rfq?.created_at
                    ? new Date(rfq.created_at).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "—"}
                </span>
              </div>

              <div className="buyer-enquiry-view-modal__field buyer-enquiry-view-modal__field--full">
                <span className="buyer-enquiry-view-modal__label">Message to Supplier</span>
                <span className="buyer-enquiry-view-modal__value">{message || "—"}</span>
              </div>

              {files.length > 0 ? (
                <div className="buyer-enquiry-view-modal__field buyer-enquiry-view-modal__field--full">
                  <span className="buyer-enquiry-view-modal__label">Attachments</span>
                  <ul className="buyer-enquiry-view-modal__files">
                    {files.map((file, idx) => {
                      const url = resolveFileUrl(file);
                      const name = String(file).split("/").pop() || `Attachment ${idx + 1}`;
                      return (
                        <li key={`${file}-${idx}`}>
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            {name}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="buyer-enquiry-view-modal__footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BuyerRfq() {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewEnquiryOpen, setViewEnquiryOpen] = useState(false);
  const [viewEnquiryRfq, setViewEnquiryRfq] = useState(null);
  const [viewEnquiryLoading, setViewEnquiryLoading] = useState(false);

  const { currencyOptions } = useCurrencyMaster();
  const { attributeUnitOptions } = useProductDetailsMaster();

  useEffect(() => {
    fetchRFQs();
  }, [filterStatus]);

  const fetchRFQs = async () => {
    try {
      setLoading(true);
      let response;
      if (filterStatus === "all") {
        response = await CommonService.get("/rfq/my/requests");
      } else {
        response = await CommonService.get(`/rfq/status/${filterStatus}`);
      }

      if (response && response.success && response.data) {
        setRfqs(Array.isArray(response.data) ? response.data : []);
      } else {
        setRfqs([]);
      }
    } catch (error) {
      console.error("Error fetching RFQs:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch RFQs";
      toast.error(errorMessage);
      setRfqs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rfqId) => {
    if (!window.confirm("Are you sure you want to delete this RFQ?")) {
      return;
    }

    try {
      const response = await CommonService.delete(`/rfq/${rfqId}`);
      if (response && response.success) {
        toast.success("RFQ deleted successfully");
        fetchRFQs();
      } else {
        throw new Error(response?.message || "Failed to delete RFQ");
      }
    } catch (error) {
      console.error("Error deleting RFQ:", error);
      const errorMessage = error?.response?.data?.message || "Failed to delete RFQ";
      toast.error(errorMessage);
    }
  };

  const openViewEnquiry = async (rfq) => {
    setViewEnquiryOpen(true);
    setViewEnquiryRfq(rfq);
    setViewEnquiryLoading(true);
    try {
      const response = await CommonService.get(`/rfq/${rfq.rfq_id}`, {}, { silent: true });
      if (response?.success && response.data) {
        setViewEnquiryRfq(response.data);
      }
    } catch (error) {
      toast.error("Failed to load enquiry details");
    } finally {
      setViewEnquiryLoading(false);
    }
  };

  const closeViewEnquiry = () => {
    setViewEnquiryOpen(false);
    setViewEnquiryRfq(null);
    setViewEnquiryLoading(false);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "active":
        return "badge bg-success";
      case "closed":
        return "badge bg-secondary";
      case "cancelled":
        return "badge bg-danger";
      default:
        return "badge bg-warning";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCurrencyLabel = (currencyValue) => {
    if (!currencyValue) return "";
    const foundByValue = currencyOptions.find(
      (opt) => String(opt.value) === String(currencyValue) || opt.currencyCode === String(currencyValue),
    );
    if (foundByValue) {
      return foundByValue.currencyCode || foundByValue.label || currencyValue;
    }
    const foundByCode = currencyOptions.find(
      (opt) => opt.currencyCode === String(currencyValue).toUpperCase(),
    );
    if (foundByCode) {
      return foundByCode.currencyCode || foundByCode.label || currencyValue;
    }
    return currencyValue;
  };

  const getUnitLabel = (unitValue) => {
    if (!unitValue) return "";
    const foundByValue = attributeUnitOptions.find((opt) => String(opt.value) === String(unitValue));
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    const foundByLabel = attributeUnitOptions.find((opt) => opt.label === String(unitValue));
    if (foundByLabel) {
      return foundByLabel.label;
    }
    return unitValue;
  };

  return (
    <div className="container-fluid py-4 buyer-rfq-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>My RFQ Requests</h2>
        <Link to="/buyer-rfq-form" className="btn btn-primary">
          <i className="fas fa-plus me-2"></i>Create New RFQ
        </Link>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-3">
              <label className="form-label">Filter by Status</label>
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All RFQs</option>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="col-md-9 d-flex align-items-end">
              <div className="ms-auto">
                <span className="text-muted">Total: {rfqs.length} RFQ(s)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : rfqs.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">No RFQs found</h5>
            <p className="text-muted">Create your first RFQ to get started</p>
            <Link to="/buyer-rfq-form" className="btn btn-primary">
              Create New RFQ
            </Link>
          </div>
        </div>
      ) : (
        <div className="row">
          {rfqs.map((rfq) => {
            const directInquiry = isDirectProductInquiry(rfq);
            return (
              <div key={rfq.rfq_id} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="card-title mb-0">{rfq.product_name || "Untitled RFQ"}</h5>
                      <span className={getStatusBadgeClass(rfq.status)}>
                        {rfq.status?.toUpperCase() || "PENDING"}
                      </span>
                    </div>

                    {directInquiry ? (
                      <span className="badge badge-enquiry-type mb-2">Product Enquiry</span>
                    ) : null}

                    {rfq.product_description && !directInquiry ? (
                      <p className="card-text text-muted small mb-2">
                        {rfq.product_description.length > 100
                          ? `${rfq.product_description.substring(0, 100)}...`
                          : rfq.product_description}
                      </p>
                    ) : null}

                    {directInquiry && rfq.special_requirements ? (
                      <p className="card-text text-muted small mb-2">
                        {(() => {
                          const { message } = parseInquirySpecialRequirements(rfq.special_requirements);
                          if (!message) return null;
                          return message.length > 100 ? `${message.substring(0, 100)}...` : message;
                        })()}
                      </p>
                    ) : null}

                    <div className="mb-2">
                      {rfq.keywords && rfq.keywords.length > 0 && !directInquiry ? (
                        <div className="mb-2">
                          {rfq.keywords.slice(0, 3).map((keyword, idx) => (
                            <span key={idx} className="badge bg-light text-dark me-1">
                              {keyword}
                            </span>
                          ))}
                          {rfq.keywords.length > 3 ? (
                            <span className="text-muted small">+{rfq.keywords.length - 3} more</span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="small text-muted mb-3">
                      <div>
                        <strong>Quantity:</strong> {rfq.product_quantity || "N/A"}{" "}
                        {getUnitLabel(rfq.quantity_unit)}
                      </div>
                      {(rfq.target_price_min || rfq.target_price_max) && (
                        <div>
                          <strong>Target Price:</strong> {formatTargetPrice(rfq, getCurrencyLabel)}
                        </div>
                      )}
                      <div>
                        <strong>Created:</strong> {formatDate(rfq.created_at)}
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      {directInquiry ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => openViewEnquiry(rfq)}
                        >
                          <i className="fas fa-eye me-1"></i>View Enquiry
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/buyer-rfq-form?edit=${rfq.rfq_id}`)}
                          >
                            <i className="fas fa-edit me-1"></i>Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(rfq.rfq_id)}
                          >
                            <i className="fas fa-trash me-1"></i>Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DirectProductInquiryViewModal
        open={viewEnquiryOpen}
        rfq={viewEnquiryRfq}
        loading={viewEnquiryLoading}
        onClose={closeViewEnquiry}
        getUnitLabel={getUnitLabel}
        getCurrencyLabel={getCurrencyLabel}
      />
    </div>
  );
}
